/**
 * "Mark as sold" dialog — records a property's disposition.
 *
 * Captures the sale price (which overwrites ARV), the closing date, and the
 * actual sell-side costs — entered either as a dollar amount or as a percent of
 * the sale price — then previews the two figures that matter at close: net cash
 * at the table and realized profit vs. all-in cost.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { CircleDollarSign, Tag } from "lucide-react";

import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/ui/date-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { NumberInput } from "@/components/ui/number-input";
import { cn } from "@/lib/utils";
import { fmtMoney } from "@/lib/calculators";

const schema = z.object({
  sale_price: z.coerce.number().min(0, "Enter a sale price"),
  closed_at: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Pick a closing date"),
  closing_costs: z.coerce.number().min(0),
});

type FormValues = z.input<typeof schema>;

/** How the selling-cost field is being entered — dollars or % of sale price. */
type CostMode = "amt" | "pct";

const pctToAmount = (pct: number, price: number) => Math.round((price * pct) / 100);
/** Inverse of `pctToAmount`, kept to 2 decimals so the field never shows 6.99998. */
const amountToPct = (amount: number, price: number) =>
  price > 0 ? Math.round((amount / price) * 10_000) / 100 : 0;

export interface MarkSoldValues {
  salePrice: number;
  closedAt: string;
  closingCosts: number;
}

interface MarkSoldDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  address: string;
  /** Current ARV — the default sale price. */
  arv: number;
  /** All-in cost EXCLUDING selling costs (purchase + rehab + closing + financing + holding). */
  allInBeforeSelling: number;
  /** Outstanding loan balance paid off at close. */
  loanPayoff: number;
  /** Seed for the selling-cost field: percent of sale price (e.g. 7). */
  defaultSellPct: number;
  isSaving: boolean;
  onConfirm: (values: MarkSoldValues) => void;
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

export function MarkSoldDialog({
  open,
  onOpenChange,
  address,
  arv,
  allInBeforeSelling,
  loanPayoff,
  defaultSellPct,
  isSaving,
  onConfirm,
}: MarkSoldDialogProps) {
  const seededCosts = pctToAmount(defaultSellPct, arv);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      sale_price: arv,
      closed_at: todayStr(),
      closing_costs: seededCosts,
    },
  });
  const [costMode, setCostMode] = useState<CostMode>("amt");
  const [costPctInput, setCostPctInput] = useState(defaultSellPct);

  useEffect(() => {
    if (open) {
      form.reset({
        sale_price: arv,
        closed_at: todayStr(),
        closing_costs: pctToAmount(defaultSellPct, arv),
      });
      setCostMode("amt");
      setCostPctInput(defaultSellPct);
    }
    // Re-seed only when the dialog (re)opens for a given property.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, arv, defaultSellPct]);

  const salePrice = Number(form.watch("sale_price")) || 0;
  const closingCosts = Number(form.watch("closing_costs")) || 0;
  const netProceeds = salePrice - closingCosts - loanPayoff;
  const profit = salePrice - (allInBeforeSelling + closingCosts);
  const costPct = salePrice > 0 ? (closingCosts / salePrice) * 100 : 0;

  // In % mode the typed percent is the source of truth, so the dollar field —
  // which still drives validation, the preview and the payload — is derived from
  // it and re-derived whenever the sale price moves.
  useEffect(() => {
    if (costMode !== "pct") return;
    form.setValue("closing_costs", pctToAmount(costPctInput, salePrice), {
      shouldDirty: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costMode, costPctInput, salePrice]);

  const handleCostModeChange = (next: CostMode) => {
    if (next === costMode) return;
    // Carry the current dollar amount over as its equivalent percent.
    if (next === "pct") setCostPctInput(amountToPct(closingCosts, salePrice));
    setCostMode(next);
  };

  const handleSubmit = form.handleSubmit((values) => {
    onConfirm({
      salePrice: Number(values.sale_price),
      closedAt: values.closed_at,
      closingCosts: Number(values.closing_costs),
    });
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-primary" aria-hidden />
            Mark as sold
          </DialogTitle>
          <DialogDescription>
            Record the sale of {address || "this property"}. The sale price becomes
            the property's ARV.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="sale_price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sale price</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">
                          $
                        </span>
                        <NumberInput
                          step="any"
                          value={Number(field.value) || 0}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                          aria-label="Sale price in dollars"
                          className="pl-7"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="closed_at"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Closing date</FormLabel>
                    <FormControl>
                      <DateInput
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="closing_costs"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>Selling costs</FormLabel>
                    <CostModeToggle mode={costMode} onModeChange={handleCostModeChange} />
                  </div>
                  <FormControl>
                    {costMode === "pct" ? (
                      <div className="relative">
                        <NumberInput
                          max={100}
                          step={0.25}
                          value={costPctInput}
                          parse={(raw) => Math.min(100, Math.max(0, Number(raw) || 0))}
                          onChange={setCostPctInput}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          aria-label="Selling costs as a percent of sale price"
                          className="pr-7"
                        />
                        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">
                          %
                        </span>
                      </div>
                    ) : (
                      <div className="relative">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">
                          $
                        </span>
                        <NumberInput
                          step="any"
                          value={Number(field.value) || 0}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                          aria-label="Selling costs in dollars"
                          className="pl-7"
                        />
                      </div>
                    )}
                  </FormControl>
                  <FormDescription>
                    Agent commission · transfer tax · seller-side closing
                    {salePrice > 0 &&
                      (costMode === "pct"
                        ? ` · ${fmtMoney(closingCosts)} of sale price`
                        : ` · ${costPct.toFixed(1)}% of sale price`)}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Outcome preview */}
            <div className="rounded-lg border border-border bg-muted/40 p-4">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <CircleDollarSign className="h-3.5 w-3.5" aria-hidden />
                At close
              </div>
              <div className="mt-3 space-y-2 text-sm">
                <PreviewRow label="Sale price" amount={salePrice} tone="pos" />
                <PreviewRow label="Selling costs" amount={-closingCosts} tone="neg" />
                <PreviewRow label="Loan payoff" amount={-loanPayoff} tone="neg" />
                <PreviewRow label="Net cash at close" amount={netProceeds} strong />
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
                <span className="text-sm font-semibold text-foreground">Realized profit</span>
                <span
                  className={cn(
                    "text-lg font-bold tabular-nums",
                    profit >= 0 ? "text-success" : "text-destructive",
                  )}
                >
                  {profit < 0 ? "−" : ""}
                  {fmtMoney(Math.abs(profit))}
                </span>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Sale price − all-in cost (incl. selling)
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? "Saving…" : "Confirm sale"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

/** `$` / `%` segmented switch shown beside the selling-costs label. */
function CostModeToggle({
  mode,
  onModeChange,
}: {
  mode: CostMode;
  onModeChange: (mode: CostMode) => void;
}) {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-md border border-border bg-muted/40 p-0.5">
      {(["amt", "pct"] as const).map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onModeChange(option)}
          className={cn(
            "rounded px-2 py-0.5 text-[11px] font-bold transition-colors",
            mode === option
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
          aria-pressed={mode === option}
          aria-label={
            option === "pct"
              ? "Enter selling costs as a percent of sale price"
              : "Enter selling costs as a dollar amount"
          }
        >
          {option === "pct" ? "%" : "$"}
        </button>
      ))}
    </div>
  );
}

function PreviewRow({
  label,
  amount,
  tone,
  strong,
}: {
  label: string;
  amount: number;
  tone?: "pos" | "neg";
  strong?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between",
        strong && "border-t border-border pt-2 font-semibold",
      )}
    >
      <span className="text-muted-foreground">{label}</span>
      <span
        className={cn(
          "font-semibold tabular-nums",
          !strong && tone === "pos" && "text-success",
          !strong && tone === "neg" && "text-destructive",
          strong && (amount >= 0 ? "text-foreground" : "text-destructive"),
        )}
      >
        {amount < 0 ? "−" : ""}
        {fmtMoney(Math.abs(amount))}
      </span>
    </div>
  );
}
